import { inject, observer } from 'mobx-react'
import useMediaQuery from '@mui/material/useMediaQuery'

import { HelpIcon, TooltipText } from '../../../../components'
import { MOBILE_VIEWPORT } from '../../../../guided-toolbox/utils/breakpoints'
import { StepContent } from '../../elements'
import { getHeadingSegments } from './headingMessage'
import {
  buildUnrestrictedRows,
  buildRestrictedRows,
  unrestrictedSellPercent,
  buildBalance,
  buildPie,
} from './tableData'
import CompanyStockTable from './CompanyStockTable'
import BalanceAccordion from './BalanceAccordion'
import { Heading, Subheading, HelpAnchor } from './style'

// Renders the heading segments, showing the emphasised fragment ("all"/"XX%") bold.
function HeadingText({ segments }) {
  return segments.map((segment, index) =>
    segment.bold ? <strong key={index}>{segment.text}</strong> : segment.text
  )
}

/**
 * Company Stock step of the step-by-step advice flow. It reflects the
 * recommendation to sell unrestricted company stock (we always recommend 100%)
 * and any modification made in the "make changes" drawer: a heading, a subheading
 * with help on why we recommend selling, a current-vs-recommended data table
 * (unrestricted then restricted holdings), and a collapsible account-balance
 * accordion with concentration pie charts.
 *
 * The step is only reachable when the company-stock feature is on and the active
 * case has company-stock holdings; Advice guards the route and skips traversal
 * otherwise (see hasCompanyStock).
 */
const CompanyStock = ({ store }) => {
  const isMobile = useMediaQuery(MOBILE_VIEWPORT)

  const activeCase = store.modifiedCase || store.recommendedCase

  // The shell shows the loader and the route guard redirects when there's no
  // company stock; this just guards a transient render before data resolves.
  if (!activeCase) {
    return null
  }

  const isModified = !!store.modifiedCase
  const unrestricted = activeCase.unrestrictedCompanyStocks || []
  const restricted = activeCase.restrictedCompanyStocks || []
  const hasUnrestricted = unrestricted.length > 0

  const headingSegments = getHeadingSegments({
    isModified,
    hasUnrestricted,
    sellPercent: unrestrictedSellPercent(unrestricted),
  })

  const { accountName, totalBalance } = buildBalance({
    accountName: store.institutionalAccount?.name,
    balance: store.primaryEmployerConfig?.balance,
  })
  const pie = buildPie({
    unrestricted,
    restricted,
    balance: store.primaryEmployerConfig?.balance,
  })

  return (
    <StepContent>
      <Heading>
        <HeadingText segments={headingSegments} />
      </Heading>

      {/* The subheading + help is hidden only when the user has ONLY restricted stock. */}
      {hasUnrestricted && (
        <Subheading>
          Why we recommend selling company stock
          <HelpAnchor>
            <HelpIcon tooltip={TooltipText.companyStockHundredPercent()} size='mediumLarge' />
          </HelpAnchor>
        </Subheading>
      )}

      <CompanyStockTable
        unrestricted={buildUnrestrictedRows(unrestricted)}
        restricted={buildRestrictedRows(restricted)}
        isModified={isModified}
        isMobile={isMobile}
      />

      <BalanceAccordion
        accountName={accountName}
        totalBalance={totalBalance}
        pie={pie}
        isModified={isModified}
        isMobile={isMobile}
      />
    </StepContent>
  )
}

export default inject('store')(observer(CompanyStock))
